/**
 * TBMuktBharat app shell — the single layout for every logged-in page.
 *
 * Hybrid top-nav + left sidebar:
 *  - The sticky header carries the brand, the 3 PRIMARY destination pills
 *    (Community Feed / Knowledge Hub / Campaigns) next to search, plus
 *    language / theme / notifications / profile / logout. There is NO "More"
 *    dropdown — the secondary destinations, Administration (admins only) and
 *    Account live in the LEFT sidebar instead.
 *  - A fixed LEFT navigation sidebar (desktop ≥1024px) renders the sidebar
 *    sections (Groups, Competitions, Messages, Notifications, Saved, Reels →
 *    Administration → Account) via `AppSidebar`.
 *  - Page content fills the area right of the sidebar.
 *
 * On mobile (<1024px) the primary pills and the sidebar are hidden; a hamburger
 * opens a slide-in drawer (focus-trapped) with the sidebar nav, and a
 * thumb-reachable bottom nav gives quick access to the 5 top actions.
 *
 * Each page wraps itself in <GovLayout>; the `showSidebar` prop is accepted but
 * ignored (deprecated — kept so existing call sites keep compiling).
 *
 * Helper components GovPageTitle / GovCard / GovButton / GovEmptyState are
 * re-exported for pages that use them.
 */
import React, { useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { LanguageSwitcher } from '@/components/portal/LanguageSwitcher'
import { AppSidebar } from '@/components/layout/AppSidebar'
import { RightRail } from '@/components/layout/RightRail'
import { useModalA11y } from '@/components/portal/useModalA11y'
import { PRIMARY_ITEMS, resolvePath } from '@/config/appNav'
import { cn } from '@/lib/utils'
import {
  Home, Search, Bell, Settings, LogOut, Menu, X,
  BookOpen, Megaphone,
} from 'lucide-react'

const useIsActive = () => {
  const location = useLocation()
  return useMemo(
    () => (path: string) => location.pathname === path || location.pathname.startsWith(path + '/'),
    [location.pathname],
  )
}

// ================================================================================
// HEADER — top nav (primary pills), no "More" dropdown (nav lives in the sidebar)
// ================================================================================
export const GovHeader = React.memo(({ onMenuClick }: { onMenuClick: () => void }) => {
  const { t } = useTranslation()
  const { user, isAuthenticated, logout } = useAuthStore()
  const navigate = useNavigate()

  return (
    <header className="sticky top-0 z-40 h-14 bg-white/95 backdrop-blur-sm border-b border-gray-100">
      <div className="flex items-center justify-between h-14 px-3 sm:px-4 lg:px-6 gap-2">
        {/* Left: hamburger (mobile) + logo + primary pills (desktop) */}
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 -ml-2 hover:bg-gray-100 rounded-full"
            aria-label={t('header.openMenu', 'Open menu')}
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
          <Link to="/feed" className="flex items-center gap-2 flex-shrink-0">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">TB</span>
            </div>
            <h1 className="hidden lg:block text-base font-bold text-gray-900 truncate">TB Mukt Bharat</h1>
          </Link>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-1 sm:gap-2">
          <button onClick={() => navigate('/search')} className="p-2 hover:bg-gray-100 rounded-full" aria-label={t('header.searchAria', 'Search')}>
            <Search className="w-5 h-5 text-gray-500" />
          </button>
          <LanguageSwitcher variant="compact" />
          <ThemeToggle variant="icon" size="md" />
          <button onClick={() => navigate('/notifications')} className="relative p-2 hover:bg-gray-100 rounded-full" aria-label={t('nav.notifications', 'Notifications')}>
            <Bell className="w-5 h-5 text-gray-500" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
          </button>
          {isAuthenticated ? (
            <>
              <Link to={`/profile/${user?.username}`} className="hidden sm:flex items-center gap-2 pl-2 border-l border-gray-200">
                <img src={user?.profileImage || '/default-avatar.svg'} alt="" className="w-7 h-7 rounded-full object-cover" loading="lazy" />
                <span className="text-sm font-medium text-gray-700">{user?.firstName}</span>
              </Link>
              <button onClick={logout} className="p-2 hover:bg-gray-100 rounded-full" aria-label={t('nav.logout', 'Logout')}>
                <LogOut className="w-4 h-4 text-gray-500" />
              </button>
            </>
          ) : (
            <Link to="/login" className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg">
              {t('nav.login', 'Login')}
            </Link>
          )}
        </div>
      </div>
    </header>
  )
})

// ================================================================================
// PRIMARY NAV ROW — the 3 primary destinations (Community Feed / Knowledge Hub /
// Campaigns) as an evenly-spaced segmented row. Sits at the top of the main content
// column (right of the left sidebar), directly above the page content — on the feed
// that is right above the "Share your TB journey" composer. Desktop only (lg+);
// mobile keeps the bottom nav for these three.
// ================================================================================
export const PrimaryNavRow = React.memo(() => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const isActive = useIsActive()

  return (
    <nav
      aria-label={t('nav.primary', 'Primary navigation')}
      className="hidden lg:flex sticky top-14 z-30 h-12 items-center bg-white border-b border-gray-100"
    >
      <div className="w-full max-w-md mx-auto px-4 flex items-center gap-2">
        {PRIMARY_ITEMS.map((item) => {
          const path = resolvePath(item, user?.username)
          const Icon = item.icon
          const active = isActive(path)
          return (
            <Link
              key={path}
              to={path}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg text-sm font-medium transition-colors',
                active ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100',
              )}
            >
              <Icon className="w-4 h-4" />
              <span className="truncate">{t(`nav.${item.key}`, item.label)}</span>
            </Link>
          )
        })}
      </div>
    </nav>
  )
})

// ================================================================================
// MOBILE BOTTOM NAV — thumb-reachable quick actions (mobile only)
// ================================================================================
export const GovBottomNav = React.memo(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const navItems = useMemo(() => [
    { icon: Home, label: t('nav.feed'), path: '/feed' },
    { icon: BookOpen, label: t('nav.knowledge'), path: '/knowledge' },
    { icon: Megaphone, label: t('nav.campaigns'), path: '/campaigns' },
    { icon: Bell, label: t('nav.notifications'), path: '/notifications' },
    { icon: Settings, label: t('nav.settings'), path: '/settings' },
  ], [t])

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30">
      <div className="flex justify-around py-1.5">
        {navItems.map((item) => {
          const Icon = item.icon
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/')
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              aria-current={isActive ? 'page' : undefined}
              className={cn('flex flex-col items-center gap-0.5 p-2 min-w-[44px]', isActive ? 'text-blue-600' : 'text-gray-400')}
            >
              <Icon className="w-5 h-5" />
              <span className="text-xs truncate max-w-[56px]">{item.label}</span>
            </button>
          )
        })}
      </div>
    </nav>
  )
})

// ================================================================================
// MOBILE DRAWER — slide-in sidebar with focus trap
// ================================================================================
const MobileDrawer = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const panelRef = useRef<HTMLDivElement>(null)
  useModalA11y(open, onClose, panelRef)

  if (!open) return null

  return (
    <div className="lg:hidden fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        className="relative w-72 max-w-[80vw] h-full bg-white shadow-xl flex flex-col"
      >
        <div className="flex items-center justify-between h-14 px-4 border-b border-gray-100 flex-shrink-0">
          <span className="font-bold text-gray-900">TB Mukt Bharat</span>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 min-h-0">
          <AppSidebar onNavigate={onClose} />
        </div>
      </div>
    </div>
  )
}

// ================================================================================
// PAGE TITLE
// ================================================================================
export const GovPageTitle = React.memo(({ title, subtitle, icon: Icon }: { title: string; subtitle?: string; icon?: React.ComponentType<{ className?: string }> }) => (
  <div className="mb-4">
    <div className="flex items-center gap-2">
      {Icon && <Icon className="w-5 h-5 text-blue-600" />}
      <h1 className="text-xl font-bold text-gray-900">{title}</h1>
    </div>
    {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
  </div>
))

// ================================================================================
// FULL LAYOUT SHELL — header + left nav sidebar + full main content
// ================================================================================
interface GovLayoutProps {
  children: React.ReactNode
  /** Deprecated/ignored — kept so existing call sites keep compiling. */
  showSidebar?: boolean
}

export const GovLayout = React.memo(({ children, showSidebar: _showSidebar = true }: GovLayoutProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false)

  return (
    <div className="min-h-screen bg-gray-50">
      <GovHeader onMenuClick={() => setDrawerOpen(true)} />

      <div className="lg:flex">
        {/* Left navigation sidebar — desktop */}
        <aside className="hidden lg:block w-64 shrink-0 sticky top-14 h-[calc(100vh-3.5rem)] border-r border-gray-100 bg-white overflow-y-auto">
          <AppSidebar />
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <PrimaryNavRow />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 pb-24 lg:pb-6">{children}</div>
        </main>

        {/* Right rail — official info, news, quick actions (desktop ≥1280px) */}
        <RightRail />
      </div>

      {/* Mobile bottom nav */}
      <GovBottomNav />

      {/* Mobile drawer (full nav) */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  )
})

// ================================================================================
// EMPTY STATE COMPONENT
// ================================================================================
export const GovEmptyState = React.memo(({ icon, title, description, action }: { icon: string; title: string; description?: string; action?: { label: string; onClick: () => void } }) => (
  <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-gray-100">
    <div className="text-5xl mb-3">{icon}</div>
    <h3 className="text-lg font-bold mb-1">{title}</h3>
    {description && <p className="text-sm text-gray-500 mb-4">{description}</p>}
    {action && (
      <button onClick={action.onClick} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">
        {action.label}
      </button>
    )}
  </div>
))

// ================================================================================
// CARD COMPONENT
// ================================================================================
interface GovCardProps {
  children: React.ReactNode
  className?: string
  onClick?: React.MouseEventHandler
}

export const GovCard = React.memo(({ children, className = '', onClick }: GovCardProps) => (
  <div
    className={`bg-white rounded-xl p-4 shadow-sm border border-gray-100 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} ${className}`}
    onClick={onClick}
  >
    {children}
  </div>
))

// ================================================================================
// BUTTON COMPONENT
// ================================================================================
export const GovButton = React.memo(({ children, variant = 'primary', size = 'md', className = '', ...props }: {
  children: React.ReactNode
  variant?: 'primary' | 'secondary' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  className?: string
  [key: string]: any
}) => {
  const baseStyles = 'font-medium rounded-lg transition-colors'
  const variantStyles = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700',
    secondary: 'border border-gray-200 text-gray-700 hover:bg-gray-50',
    ghost: 'text-gray-600 hover:bg-gray-100',
  }
  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
  }

  return (
    <button className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`} {...props}>
      {children}
    </button>
  )
})

export default GovLayout